// Copyright © 2025–2026 Maestro Design & Management AB. Proprietary and confidential; see LICENSE.
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useLoadScript, GoogleMap, Rectangle, Polygon, Polyline, Marker, InfoWindow } from '@react-google-maps/api';
import axios from 'axios';

import { GOOGLE_MAPS_API_KEY, DEFAULT_LOCATION } from '../constants/placeTypes';
import TopBar from './TopBar';
import API_BASE_URL from '../config/api';
import OpenSpaceOnboarding, { hasSeenOnboarding, markOnboardingSeen } from './OpenSpaceOnboarding';
import { M_PER_DEG_LAT, boundsAreaKm2, boundsToBox, boxToBounds, lookupAreaName } from '../utils/selectionBox';
import './OpenSpaceAnalyzer.css';

// Data-licence attribution. Lantmäteriet Byggnad is CC BY 4.0 and OSM is ODbL:
// both require the credit wherever the data or a derived result is shown or
// exported (results panel, PNG export, GeoJSON `attribution`).
const DATA_ATTRIBUTION = (buildingSource) =>
  (buildingSource === 'lm+osm' ? '© Lantmäteriet (CC BY 4.0) · ' : '') +
  '© OpenStreetMap-bidragsgivare (ODbL) · Bakgrundskarta © Google';

// Same libraries array as the other map pages - the Google Maps JS loader
// may only ever be called with ONE set of options per page load.
const libraries = ['places', 'geometry'];

const MAX_AREA_KM2 = 4.0;

// Map styling: open spaces (>= 45 x 45 m) get a yellow border with a red tint, small parks
// (OSM park-like ground below the limit) a green one.
const OPEN_STYLE = { stroke: '#FFF23C', fill: '#E53935', fillOpacity: 0.25, weight: 3, badge: '#9C8600' };
const PARK_STYLE = { stroke: '#00E676', fill: '#00E676', fillOpacity: 0.12, weight: 2, badge: '#1B7F3B' };
const isPark = (f) => f.properties?.kind === 'park';
// Small-park layer switched off 2026-09-07 (backend no longer calls
// detect_small_parks). Rendering code is kept; flip this to re-enable.
const SHOW_PARKS = false;
// Illustrationsplan layer/scenario switched off 2026-09-07 (user decision):
// only the byggrätt (max) scenario is shown. Backend keeps plan_reader.USE_ILLUSTRATION.
const SHOW_ILLUSTRATION = false;
// Detaljplan upload removed from the UI (user decision 2026-09-24): no
// "Lägg till plankarta" button, no scenario toggle, no plan overlay - also for
// earlier analyses that were run with a plan. The backend (plan-upload,
// plan-locate, openSpacePlan, _run_plan_scenarios) is untouched; flip this
// to bring the feature back.
const SHOW_PLAN = false;

// Compass name for a heading in degrees (0 = north, clockwise).
const COMPASS_SV = ['N', 'NO', 'O', 'SO', 'S', 'SV', 'V', 'NV'];
const compassName = (deg) => COMPASS_SV[Math.round((((deg % 360) + 360) % 360) / 45) % 8];
const normHeading = (deg) => ((deg % 360) + 360) % 360;

const formatArea = (m2) => {
  if (m2 >= 10000) return `${(m2 / 10000).toFixed(1).replace('.', ',')} ha`;
  return `${m2.toLocaleString('sv-SE')} m²`;
};

const OpenSpaceAnalyzer = () => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries
  });

  const [selecting, setSelecting] = useState(false);
  const [cornerA, setCornerA] = useState(null);
  const [hoverCorner, setHoverCorner] = useState(null);  // rubber band
  const [lockedBox, setLockedBox] = useState(null);  // {wM, hM, dLat, dLng}
  const [mapCenter, setMapCenter] = useState(DEFAULT_LOCATION);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [status, setStatus] = useState('Välj ett område på kartan för att hitta ytor större än 45 × 45 m utan byggnader.');
  const [result, setResult] = useState(null);      // geojson FeatureCollection
  const [summary, setSummary] = useState(null);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [previous, setPrevious] = useState([]);
  const [error, setError] = useState('');
  // Layer visibility (map + PNG export). Open spaces are always shown; the
  // small-park layer is disabled (SHOW_PARKS) - the backend no longer emits it.
  const showOpen = true;
  const showParks = SHOW_PARKS;
  const [showNumbers, setShowNumbers] = useState(true);
  // Detaljplan overlay: an uploaded plankarta (PDF/ZIP) is georeferenced by
  // the backend and the detector re-runs with the plan's byggrätt / drawn
  // buildings in place of today's buildings inside the plan area.
  const [planFile, setPlanFile] = useState(null);
  const [planUpload, setPlanUpload] = useState(null);   // {planKey, filename} once uploaded
  const [planLocating, setPlanLocating] = useState(false);
  // Elapsed-seconds counter + expected duration for the busy states
  // (upload, locate, analysis) shown next to the spinner in the status line.
  const [busy, setBusy] = useState(null);   // {since: ms, expect: 'normalt 5–20 s'} | null
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!busy) { setElapsed(0); return undefined; }
    setElapsed(0);
    const id = setInterval(() => setElapsed(Math.round((Date.now() - busy.since) / 1000)), 1000);
    return () => clearInterval(id);
  }, [busy]);
  const startBusy = (expect) => setBusy({ since: Date.now(), expect });
  const [plan, setPlan] = useState(null);                 // job.plan summary
  const [planGeojson, setPlanGeojson] = useState(null);   // planomrade/byggratt/plan_building
  const [scenarioGeojson, setScenarioGeojson] = useState({});
  const [scenario, setScenario] = useState('now');        // 'now' | 'byggratt' | 'illustration'
  const [showPlan, setShowPlan] = useState(true);
  const planInputRef = useRef(null);
  // Onboarding film: opens automatically on the first visit, then via the button.
  const [onboardingOpen, setOnboardingOpen] = useState(() => !hasSeenOnboarding());
  const closeOnboarding = () => { markOnboardingSeen(); setOnboardingOpen(false); };

  // Street View mode: click 1 = viewpoint, click 2 = direction target.
  const [svMode, setSvMode] = useState(false);
  const [svPoint, setSvPoint] = useState(null);       // {lat, lng}
  const [svTarget, setSvTarget] = useState(null);     // {lat, lng}
  const [svHover, setSvHover] = useState(null);       // rubber-band end
  const [svOpen, setSvOpen] = useState(false);        // modal visible
  const [svPano, setSvPano] = useState(null);         // {panoId, position, heading}
  const [svError, setSvError] = useState('');
  const svDivRef = useRef(null);
  const svPanoramaRef = useRef(null);
  const svPointRef = useRef(null);
  svPointRef.current = svPoint;

  const mapRef = useRef(null);
  const rectRef = useRef(null);
  const mapCenterRef = useRef(DEFAULT_LOCATION);
  const lockedBoxRef = useRef(null);
  lockedBoxRef.current = lockedBox;

  const liveMapCenter = () => {
    const c = mapRef.current?.getCenter();
    return c ? { lat: c.lat(), lng: c.lng() } : mapCenterRef.current;
  };

  const handleCenterChanged = () => {
    const c = liveMapCenter();
    mapCenterRef.current = c;
    // Re-render only when a locked box must follow the screen or the
    // direction arrow must be re-scaled to the new viewport.
    if (lockedBoxRef.current || svPointRef.current) setMapCenter(c);
  };

  useEffect(() => {
    const loadPrevious = async () => {
      try {
        const resp = await axios.get(`${API_BASE_URL}/open-space/analyses`, { withCredentials: true });
        setPrevious((resp.data.analyses || []).filter(a => a.status === 'completed').slice(0, 10));
      } catch (e) {
        // Non-critical - list stays empty
      }
    };
    loadPrevious();
  }, []);

  // Find the nearest outdoor panorama and open the modal looking from it
  // towards `target`. Heading is computed from the PANORAMA position (which
  // may be up to 50 m from the clicked point), so the view really faces
  // the chosen target.
  const openStreetView = useCallback((point, target) => {
    const g = window.google.maps;
    setSvError('');
    setSvPano(null);
    setSvOpen(true);
    const svc = new g.StreetViewService();
    svc.getPanorama({
      location: point,
      radius: 50,
      source: g.StreetViewSource.OUTDOOR,
    }, (data, status) => {
      if (status !== g.StreetViewStatus.OK || !data?.location?.latLng) {
        setSvError('Ingen gatuvy finns inom 50 m från punkten. Prova en punkt närmare en gata.');
        return;
      }
      const pos = data.location.latLng;
      const heading = normHeading(g.geometry.spherical.computeHeading(pos, new g.LatLng(target.lat, target.lng)));
      setSvPano({
        panoId: data.location.pano,
        position: { lat: pos.lat(), lng: pos.lng() },
        heading,
      });
    });
  }, []);

  const handleMapClick = useCallback((e) => {
    const p = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    if (svMode) {
      if (!svPoint) {
        setSvPoint(p);
        setSvTarget(null);
        setSvHover(null);  // arrow points north until the mouse moves
        setStatus('Pilen visar riktningen - flytta musen och klicka för att öppna gatuvyn.');
      } else {
        setSvTarget(p);
        setSvHover(null);
        setSvMode(false);
        setStatus('Gatuvy öppnad. Klicka på "Gatuvy" igen för att välja en ny punkt.');
        openStreetView(svPoint, p);
      }
      return;
    }
    if (!selecting) return;
    if (!cornerA) {
      setCornerA(p);
      setHoverCorner(p);
      setStatus('Dra ut rektangeln och klicka för att sätta motsatta hörnet.');
    } else {
      const b = {
        south: Math.min(cornerA.lat, p.lat),
        north: Math.max(cornerA.lat, p.lat),
        west: Math.min(cornerA.lng, p.lng),
        east: Math.max(cornerA.lng, p.lng),
      };
      setLockedBox(boundsToBox(b, liveMapCenter()));
      setMapCenter(liveMapCenter());
      setCornerA(null);
      setHoverCorner(null);
      setSelecting(false);
      setStatus('Område valt. Panorera kartan för att analysera samma storlek någon annanstans - rutan följer med. Dra/justera rutan eller klicka på Analysera.');
    }
  }, [selecting, cornerA, svMode, svPoint, openStreetView]);

  const handleMouseMove = useCallback((e) => {
    if (selecting && cornerA) {
      setHoverCorner({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    } else if (svMode && svPoint) {
      setSvHover({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    }
  }, [selecting, cornerA, svMode, svPoint]);

  const startStreetView = () => {
    setSelecting(false);
    setCornerA(null);
    setHoverCorner(null);
    setSvMode(true);
    setSvPoint(null);
    setSvTarget(null);
    setSvHover(null);
    setSvOpen(false);
    setSvPano(null);
    setSvError('');
    setStatus('Klicka på kartan där du vill stå.');
  };

  const closeStreetView = () => {
    setSvOpen(false);
    setSvPano(null);
  };

  // Mount the panorama in the modal. Raw Maps API: the react-google-maps
  // StreetViewPanorama must live inside a GoogleMap, which a modal has no
  // use for. The pano div is only rendered once svPano is known, so a new
  // instance is built for every lookup.
  useEffect(() => {
    if (!svOpen || !svPano || !svDivRef.current) return;
    const g = window.google.maps;
    svPanoramaRef.current = new g.StreetViewPanorama(svDivRef.current, {
      pano: svPano.panoId,
      pov: { heading: svPano.heading, pitch: 0 },
      zoom: 1,
      addressControl: false,
      enableCloseButton: false,
      fullscreenControl: true,
      motionTracking: false,
      motionTrackingControl: false,
    });
  }, [svOpen, svPano]);

  useEffect(() => {
    if (!svOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeStreetView(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [svOpen]);

  const startSelection = () => {
    setSvMode(false);
    setSvHover(null);
    setSelecting(true);
    setCornerA(null);
    setHoverCorner(null);
    setLockedBox(null);
    setResult(null);
    setSummary(null);
    setSelectedFeature(null);
    setError('');
    setStatus('Klicka på kartan för att sätta rektangelns första hörn.');
  };

  const currentBounds = () => (
    lockedBoxRef.current
      ? boxToBounds(lockedBoxRef.current, mapCenterRef.current)
      : null
  );

  // Fires both on user drag/resize of the rectangle AND on our own
  // re-renders - only adopt the change when it differs meaningfully from
  // what we rendered (> ~1 m), otherwise we'd loop.
  const handleRectBoundsChanged = () => {
    const rb = rectRef.current?.getBounds();
    const box = lockedBoxRef.current;
    if (!rb || !box) return;
    const b = {
      south: rb.getSouthWest().lat(), west: rb.getSouthWest().lng(),
      north: rb.getNorthEast().lat(), east: rb.getNorthEast().lng(),
    };
    const expected = boxToBounds(box, mapCenterRef.current);
    const eps = 1.5e-5; // ~1.5 m in latitude
    if (['south', 'west', 'north', 'east'].some(k => Math.abs(b[k] - expected[k]) > eps)) {
      setLockedBox(boundsToBox(b, mapCenterRef.current));
    }
  };

  const pollStatus = async (jobId) => {
    // Worker may retry Overpass mirrors for up to ~7 min - poll for 11.
    let misses = 0;
    for (let attempt = 0; attempt < 260; attempt++) {
      await new Promise(r => setTimeout(r, 2500));
      let resp;
      try {
        resp = await axios.get(`${API_BASE_URL}/open-space/status/${jobId}`, { withCredentials: true });
        misses = 0;
      } catch (e) {
        // A dropped poll must not abandon a job that is still running.
        if (++misses >= 4 || e.response?.status === 401 || e.response?.status === 403) throw e;
        continue;
      }
      const job = resp.data;
      if (job.status === 'completed') return job;
      if (job.status === 'failed') throw new Error(job.error || 'Analysen misslyckades');
      setStatus(job.progressNote
        || (job.status === 'processing'
          ? 'Hämtar kartdata och analyserar ytor…'
          : 'Analysen köar…'));
    }
    throw new Error('Analysen tog för lång tid - försök igen.');
  };

  const showJobResult = (job) => {
    setResult(job.geojson);
    setPlan(SHOW_PLAN ? (job.plan || null) : null);
    setPlanGeojson(SHOW_PLAN ? (job.planGeojson || null) : null);
    setScenarioGeojson(SHOW_PLAN ? (job.scenarioGeojson || {}) : {});
    setScenario('now');
    setSummary({
      count: job.spaceCount ?? (job.geojson?.features?.length || 0),
      totalAreaM2: job.totalAreaM2 ?? 0,
      parkCount: job.parkCount ?? (job.geojson?.features || []).filter(isPark).length,
      treeSource: job.treeSource,
      buildingSource: job.buildingSource,
      buildingCount: job.buildingCount,
      footprintAreaM2: job.footprintAreaM2,
      urbanCoverageRatio: job.urbanCoverageRatio,
      buildingSizeRatio: job.buildingSizeRatio,
      meanFootprintM2: job.meanFootprintM2,
      openAreaInSelectionM2: job.openAreaInSelectionM2,
      emptySpaceRatio: job.emptySpaceRatio,
    });
    if (job.bbox) {
      // Anchor the screen-pinned box on the analysed area (offset 0 from
      // the centre fitBounds is about to move to).
      const mid = {
        lat: (job.bbox.south + job.bbox.north) / 2,
        lng: (job.bbox.west + job.bbox.east) / 2,
      };
      setLockedBox(boundsToBox(job.bbox, mid));
      setMapCenter(mid);
      mapCenterRef.current = mid;
    }
    const all = job.geojson?.features || [];
    const n = all.filter(f => !isPark(f)).length;
    const p = all.filter(isPark).length;
    const parkNote = p > 0 ? ` Dessutom ${p} mindre parker/grönytor under gränsen.` : '';
    setStatus((n > 0
      ? `Hittade ${n} ytor utan byggnader (minst 45 × 45 m).`
      : 'Inga byggnadsfria ytor på minst 45 × 45 m hittades i området.') + parkNote);
    if (mapRef.current && job.bbox) {
      const gb = new window.google.maps.LatLngBounds(
        { lat: job.bbox.south, lng: job.bbox.west },
        { lat: job.bbox.north, lng: job.bbox.east }
      );
      mapRef.current.fitBounds(gb);
    }
  };

  // Upload the plan straight away and ask the backend where it is: the map
  // jumps to the plan and a selection box (plan area + 60 m) is proposed,
  // so the user only has to adjust it and press Analysera.
  const uploadPlan = async (file) => {
    const up = await axios.post(
      `${API_BASE_URL}/open-space/plan-upload`, { filename: file.name }, { withCredentials: true });
    await axios.put(up.data.uploadUrl, file, {
      headers: { 'Content-Type': up.data.contentType }, withCredentials: false });
    return { planKey: up.data.planKey, filename: file.name };
  };

  const handlePlanChosen = async (file) => {
    setPlanFile(file);
    setPlanUpload(null);
    if (!file) return;
    setError('');
    setPlanLocating(true);
    const mb = file.size / 1048576;
    // ~1.5 MB/s is a typical upstream; the estimate is only a hint
    startBusy(`ca ${Math.max(2, Math.round(mb / 1.5 + 1))} s`);
    setStatus(`Laddar upp ${file.name} (${mb.toLocaleString('sv-SE', { maximumFractionDigits: 1 })} MB)…`);
    try {
      const up = await uploadPlan(file);
      setPlanUpload(up);
      startBusy('normalt 5–20 s');
      setStatus('Placerar plankartan: läser koordinatkryss, annars matchas grundkartan mot Lantmäteriets byggnader…');
      const c = liveMapCenter();
      const resp = await axios.post(
        `${API_BASE_URL}/open-space/plan-locate`,
        { planKey: up.planKey, filename: up.filename, seed: { lat: c.lat, lng: c.lng } },
        { withCredentials: true });
      const loc = resp.data;
      if (loc.bounds) {
        setPlanGeojson({ type: 'FeatureCollection', features: loc.features || [] });
        setPlan({ filename: up.filename, georef: loc.georef, stats: loc.stats, illustration: loc.illustration, scenarios: {} });
        setResult(null);
        setSummary(null);
        setSelectedFeature(null);
        // Proposed box: plan area + 60 m margin, at least 150 m per side
        const b = loc.bounds;
        const midLat = (b.south + b.north) / 2;
        const mPerLng = M_PER_DEG_LAT * Math.cos(midLat * Math.PI / 180);
        const wM = Math.max(150, (b.east - b.west) * mPerLng + 120);
        const hM = Math.max(150, (b.north - b.south) * M_PER_DEG_LAT + 120);
        const mid = { lat: midLat, lng: (b.west + b.east) / 2 };
        setMapCenter(mid);
        mapCenterRef.current = mid;
        setLockedBox({ wM, hM, dLat: 0, dLng: 0 });
        setSelecting(false);
        setCornerA(null);
        if (mapRef.current) {
          const halfLat = hM / M_PER_DEG_LAT / 2, halfLng = wM / mPerLng / 2;
          mapRef.current.fitBounds(new window.google.maps.LatLngBounds(
            { lat: mid.lat - halfLat, lng: mid.lng - halfLng },
            { lat: mid.lat + halfLat, lng: mid.lng + halfLng }), 60);
        }
        const how = loc.georef?.method === 'crosses' ? 'via koordinatkryss'
          : loc.georef?.method?.startsWith('grid') ? 'via koordinatnätet'
            : 'genom matchning mot Lantmäteriets byggnader';
        setStatus(`Plankartan placerad ${how}. Justera rutan vid behov och tryck Analysera.`);
      } else {
        setStatus('');
        setError(`Plankartan kunde inte placeras automatiskt: ${loc.error || 'okänt fel'} Rita en ruta över planområdet och tryck Analysera – då görs ett nytt försök nära rutan.`);
      }
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Okänt fel';
      setStatus('');
      setError(`Plankartan kunde inte placeras: ${msg}`);
    } finally {
      setPlanLocating(false);
      setBusy(null);
    }
  };

  const handleAnalyze = async () => {
    const b = currentBounds();
    if (!b) return;
    const areaKm2 = boundsAreaKm2(b);
    if (areaKm2 > MAX_AREA_KM2) {
      setError(`Området är ${areaKm2.toFixed(1)} km² - max ${MAX_AREA_KM2} km². Gör rektangeln mindre.`);
      return;
    }
    setError('');
    setIsAnalyzing(true);
    setResult(null);
    setSelectedFeature(null);
    startBusy(planFile ? 'normalt 30–90 s med plankarta' : 'normalt 10–60 s');
    setStatus('Startar analys…');
    try {
      // Same area name as the PNG export, so the saved run is recognisable
      // in "Tidigare analyser". Best-effort: empty when Nominatim fails.
      const areaName = await lookupAreaName(
        (b.south + b.north) / 2, (b.west + b.east) / 2);
      const startBody = { bbox: b, name: areaName || '' };
      if (planFile) {
        // Already uploaded when chosen (presigned PUT straight to S3); the
        // key rides with start. Re-upload only if that upload failed.
        let up = planUpload;
        if (!up) {
          setStatus('Laddar upp plankartan…');
          up = await uploadPlan(planFile);
          setPlanUpload(up);
        }
        startBody.planKey = up.planKey;
        startBody.planFilename = up.filename;
      }
      const startResp = await axios.post(
        `${API_BASE_URL}/open-space/start`,
        startBody,
        { withCredentials: true }
      );
      const job = await pollStatus(startResp.data.jobId);
      showJobResult(job);
      setPrevious(prev => [job, ...prev].slice(0, 10));
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Okänt fel';
      setError(`Analysen misslyckades: ${msg}`);
      setStatus('');
    } finally {
      setIsAnalyzing(false);
      setBusy(null);
    }
  };

  const loadPreviousAnalysis = async (analysisId) => {
    setError('');
    setIsAnalyzing(true);
    setStatus('Hämtar tidigare analys…');
    try {
      const resp = await axios.get(`${API_BASE_URL}/open-space/status/${analysisId}`, { withCredentials: true });
      showJobResult(resp.data);
    } catch (e) {
      setError('Kunde inte hämta analysen.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Compose a PNG: Google Static Maps image as background (same map type as
  // the live map - satellite/hybrid/roadmap/terrain, so the user's "Etiketter"
  // choice carries over), our polygons/box/labels drawn on top with canvas 2D
  // (Web Mercator math).
  const exportPng = async () => {
    const b = currentBounds();
    if (!b) return;
    setError('');
    try {
      // Cover the box and all result polygons.
      let s = b.south, n = b.north, w = b.west, e = b.east;
      features.forEach(f => f.geometry.coordinates[0].forEach(([lng, lat]) => {
        s = Math.min(s, lat); n = Math.max(n, lat);
        w = Math.min(w, lng); e = Math.max(e, lng);
      }));
      const SIZE = 640, SCALE = 2, PX = SIZE * SCALE;
      const cLat = (s + n) / 2, cLng = (w + e) / 2;

      const mercX = (lng, zoom) => (lng + 180) / 360 * 256 * Math.pow(2, zoom);
      const mercY = (lat, zoom) => {
        const r = lat * Math.PI / 180;
        return (1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2
          * 256 * Math.pow(2, zoom);
      };
      let zoom = 20;
      while (zoom > 3) {
        const spanX = Math.abs(mercX(e, zoom) - mercX(w, zoom)) * SCALE;
        const spanY = Math.abs(mercY(s, zoom) - mercY(n, zoom)) * SCALE;
        if (spanX < PX * 0.92 && spanY < PX * 0.92) break;
        zoom -= 1;
      }
      const toPx = (lat, lng) => ({
        x: (mercX(lng, zoom) - mercX(cLng, zoom)) * SCALE + PX / 2,
        y: (mercY(lat, zoom) - mercY(cLat, zoom)) * SCALE + PX / 2,
      });

      const liveType = mapRef.current?.getMapTypeId?.() || 'hybrid';
      const mapType = ['roadmap', 'satellite', 'hybrid', 'terrain'].includes(liveType) ? liveType : 'hybrid';
      const url = `https://maps.googleapis.com/maps/api/staticmap?center=${cLat},${cLng}` +
        `&zoom=${zoom}&size=${SIZE}x${SIZE}&scale=${SCALE}&maptype=${mapType}&key=${GOOGLE_MAPS_API_KEY}`;
      const img = await new Promise((resolve, reject) => {
        const im = new Image();
        im.crossOrigin = 'anonymous';
        im.onload = () => resolve(im);
        im.onerror = () => reject(new Error('Kunde inte hämta kartbilden (Static Maps)'));
        im.src = url;
      });

      const canvas = document.createElement('canvas');
      canvas.width = PX; canvas.height = PX;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, PX, PX);

      // Result polygons + number badges (open spaces yellow, parks green)
      const drawLayer = (list, style, prefix) => list.forEach((f, i) => {
        // All rings: [0] is the outline, the rest are holes (buildings
        // enclosed by the space) - even-odd fill leaves them unpainted.
        const rings = f.geometry.coordinates.map(r => r.map(([lng, lat]) => toPx(lat, lng)));
        const ring = rings[0];
        ctx.beginPath();
        rings.forEach(r => {
          r.forEach((p, j) => (j ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
          ctx.closePath();
        });
        ctx.globalAlpha = style.fillOpacity;
        ctx.fillStyle = style.fill;
        ctx.fill('evenodd');
        ctx.globalAlpha = 1;
        ctx.strokeStyle = style.stroke;
        ctx.lineWidth = style.weight;
        ctx.stroke();
        if (!showNumbers) return;
        const c = ring.reduce((a, p) => ({ x: a.x + p.x / ring.length, y: a.y + p.y / ring.length }), { x: 0, y: 0 });
        ctx.beginPath();
        ctx.arc(c.x, c.y, 16, 0, Math.PI * 2);
        ctx.fillStyle = style.badge;
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 16px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`${prefix}${i + 1}`, c.x, c.y + 1);
      });
      // Plan overlay under the result polygons: byggrätt orange, illustrated
      // buildings brown, plan area outline dark.
      const drawPlain = (list, fill, alpha, stroke, width) => list.forEach((f) => {
        ctx.beginPath();
        f.geometry.coordinates.forEach(r => {
          r.forEach(([lng, lat], j) => { const q = toPx(lat, lng); j ? ctx.lineTo(q.x, q.y) : ctx.moveTo(q.x, q.y); });
          ctx.closePath();
        });
        if (fill) { ctx.globalAlpha = alpha; ctx.fillStyle = fill; ctx.fill('evenodd'); ctx.globalAlpha = 1; }
        ctx.strokeStyle = stroke; ctx.lineWidth = width; ctx.stroke();
      });
      drawPlain(planLayer('byggratt'), '#FF8F00', scenario === 'byggratt' ? 0.55 : 0.25, '#E65100', 2);
      if (SHOW_ILLUSTRATION) drawPlain(planLayer('plan_building'), '#6D4C41', scenario === 'illustration' ? 0.7 : 0.35, '#3E2723', 1.5);
      drawPlain(planLayer('planomrade'), null, 0, '#37474F', 4);
      if (showOpen) drawLayer(openFeatures, OPEN_STYLE, '');
      if (showParks) drawLayer(parkFeatures, PARK_STYLE, 'P');

      // Selection box + dimensions label
      const p1 = toPx(b.north, b.west), p2 = toPx(b.south, b.east);
      ctx.strokeStyle = '#2979FF';
      ctx.lineWidth = 5;
      ctx.strokeRect(p1.x, p1.y, p2.x - p1.x, p2.y - p1.y);
      if (dimsLabel) {
        ctx.font = 'bold 24px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.lineWidth = 5;
        ctx.strokeStyle = '#fff';
        ctx.strokeText(dimsLabel, (p1.x + p2.x) / 2, p1.y - 8);
        ctx.fillStyle = '#2979FF';
        ctx.fillText(dimsLabel, (p1.x + p2.x) / 2, p1.y - 8);
      }

      // Metrics overlay, top-right: the three selection measurements.
      const fmtN = (v, o) => (v == null ? '–' : v.toLocaleString('sv-SE', o));
      const rows = [];
      if (view?.emptySpaceRatio != null) rows.push([
        'Empty Space Ratio', `${fmtN(summary.emptySpaceRatio, { maximumFractionDigits: 1 })} %`,
        `${fmtN(summary.openAreaInSelectionM2 || 0)} m² öppna ytor i rutan`]);
      if (view?.urbanCoverageRatio != null) rows.push([
        'Urban Coverage Ratio', `${fmtN(summary.urbanCoverageRatio, { maximumFractionDigits: 1 })} %`,
        `${fmtN(summary.footprintAreaM2)} m² byggnadsavtryck i rutan`]);
      if (view?.buildingSizeRatio != null) rows.push([
        'Building Size Ratio', fmtN(summary.buildingSizeRatio, { maximumFractionDigits: 2 }),
        `per 100 m² avtryck · ${fmtN(summary.buildingCount)} byggnader, snitt ${fmtN(summary.meanFootprintM2)} m²`]);
      if (scenarioInfo) rows.unshift(['Scenario', SCENARIO_LABELS[scenario], `idag: ESR ${fmtPct(summary?.emptySpaceRatio)}, UCR ${fmtPct(summary?.urbanCoverageRatio)}`]);
      if (rows.length) {
        const PAD = 18, W = 500, ROW_H = 74, X = PX - W - 20, Y = 20;
        const H = PAD * 2 + rows.length * ROW_H - 26;
        ctx.globalAlpha = 0.88;
        ctx.fillStyle = '#fff';
        ctx.fillRect(X, Y, W, H);
        ctx.globalAlpha = 1;
        ctx.strokeStyle = '#2979FF';
        ctx.lineWidth = 3;
        ctx.strokeRect(X, Y, W, H);
        ctx.textAlign = 'left';
        rows.forEach(([title, value, sub], i) => {
          const y = Y + PAD + i * ROW_H;
          ctx.textBaseline = 'top';
          ctx.fillStyle = '#555';
          ctx.font = '600 15px sans-serif';
          ctx.fillText(title.toUpperCase(), X + PAD, y);
          ctx.fillStyle = '#111';
          ctx.font = 'bold 30px sans-serif';
          ctx.fillText(value, X + PAD, y + 18);
          ctx.fillStyle = '#444';
          ctx.font = '15px sans-serif';
          ctx.fillText(sub, X + PAD + 110, y + 30);
        });
      }

      // Licence attribution (Lantmäteriet CC BY / OSM ODbL), top-left - the
      // bottom edge is reserved for Google's own logo and "Map data" marks.
      {
        const txt = DATA_ATTRIBUTION(view?.buildingSource);
        ctx.font = '15px sans-serif';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        const tw = ctx.measureText(txt).width;
        ctx.globalAlpha = 0.85;
        ctx.fillStyle = '#fff';
        ctx.fillRect(10, 10, tw + 16, 26);
        ctx.globalAlpha = 1;
        ctx.fillStyle = '#222';
        ctx.fillText(txt, 18, 15);
      }
      const blob = await new Promise(r => canvas.toBlob(r, 'image/png'));
      const areaName = await lookupAreaName(cLat, cLng);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const ts = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
      a.download = `${areaName || 'Tomma ytor'}${scenarioInfo ? ` (${scenario})` : ''} ${ts}.png`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      setError(`PNG-export misslyckades: ${err.message}`);
    }
  };

  const focusFeature = (feature) => {
    setSelectedFeature(feature);
    if (mapRef.current) {
      const gb = new window.google.maps.LatLngBounds();
      feature.geometry.coordinates[0].forEach(([lng, lat]) => gb.extend({ lat, lng }));
      mapRef.current.fitBounds(gb, 80);
    }
  };

  // Direction arrow: a fixed on-screen length (a share of the visible map
  // height) from the viewpoint towards the target/cursor, north by default,
  // so it is visible the moment the point is placed.
  const svArrowPath = () => {
    if (!svPoint) return null;
    const g = window.google.maps;
    const towards = svTarget || svHover;
    const from = new g.LatLng(svPoint.lat, svPoint.lng);
    const heading = towards
      ? g.geometry.spherical.computeHeading(from, new g.LatLng(towards.lat, towards.lng))
      : 0;
    const b = mapRef.current?.getBounds();
    const spanM = b
      ? g.geometry.spherical.computeDistanceBetween(
          new g.LatLng(b.getSouthWest().lat(), b.getCenter().lng()),
          new g.LatLng(b.getNorthEast().lat(), b.getCenter().lng()))
      : 1500;
    const to = g.geometry.spherical.computeOffset(from, spanM * 0.12, heading);
    return [svPoint, { lat: to.lat(), lng: to.lng() }];
  };

  const featureCenter = (feature) => {
    const ring = feature.geometry.coordinates[0];
    let lat = 0, lng = 0;
    ring.forEach(([x, y]) => { lng += x; lat += y; });
    return { lat: lat / ring.length, lng: lng / ring.length };
  };

  const bounds = lockedBox ? boxToBounds(lockedBox, mapCenter) : null;
  const selBounds = bounds || (cornerA && hoverCorner ? {
    south: Math.min(cornerA.lat, hoverCorner.lat),
    north: Math.max(cornerA.lat, hoverCorner.lat),
    west: Math.min(cornerA.lng, hoverCorner.lng),
    east: Math.max(cornerA.lng, hoverCorner.lng),
  } : null);
  const selAreaKm2 = bounds ? boundsAreaKm2(bounds) : 0;
  const dimsLabel = selBounds
    ? `${Math.round((selBounds.east - selBounds.west) * M_PER_DEG_LAT
        * Math.cos(((selBounds.south + selBounds.north) / 2) * Math.PI / 180))} × ${
        Math.round((selBounds.north - selBounds.south) * M_PER_DEG_LAT)} m`
    : '';
  // Which open-space result is on screen: today's, or one of the plan
  // scenarios (same detector, buildings inside the plan area swapped).
  const scenarioInfo = scenario !== 'now' ? plan?.scenarios?.[scenario] : null;
  const displayed = scenario === 'now' ? result : (scenarioGeojson[scenario] || null);
  const features = displayed?.features || [];
  // Older results have no `kind` - treat them as open spaces. Other kinds
  // (building/entrance live in a separate buildings.geojson) are ignored.
  const openFeatures = features.filter(f => !f.properties?.kind || f.properties.kind === 'open');
  const parkFeatures = SHOW_PARKS ? features.filter(isPark) : [];
  // Metrics for the displayed scenario (Building Size Ratio only exists for
  // today - the plan gives footprints, not building counts).
  const view = (scenarioInfo && summary) ? {
    ...summary,
    count: scenarioInfo.spaceCount,
    totalAreaM2: scenarioInfo.totalAreaM2,
    openAreaInSelectionM2: scenarioInfo.openAreaInSelectionM2,
    emptySpaceRatio: scenarioInfo.emptySpaceRatio,
    footprintAreaM2: scenarioInfo.footprintAreaM2,
    urbanCoverageRatio: scenarioInfo.urbanCoverageRatio,
    buildingSizeRatio: null,
  } : summary;
  const planFeatures = (showPlan && planGeojson?.features) || [];
  const planLayer = (kind) => planFeatures.filter(f => f.properties?.kind === kind);
  // Illustration scenario hidden (user decision 2026-09-07): only byggrätt.
  const scenarioNames = Object.keys(plan?.scenarios || {}).filter(k => !plan.scenarios[k].error && k !== 'illustration');
  const SCENARIO_LABELS = { byggratt: 'Efter plan – byggrätt (max)', illustration: 'Efter plan – illustration' };
  const fmtPct = (v) => (v == null ? '–' : `${v.toLocaleString('sv-SE', { maximumFractionDigits: 1 })} %`);

  if (loadError) return <div className="error">Kartan kunde inte laddas: {loadError.message}</div>;
  if (!isLoaded) return <div className="status">Laddar karta…</div>;

  return (
    <>
      <TopBar />
      <div className="open-space ut-page">
        <div className="ut-page-head">
          <div className="ut-eyebrow">Tomma ytor</div>
          <h1>Hitta tomma ytor i staden</h1>
          <p className="ut-lede">
            Markera ett område så identifieras sammanhängande markytor där en kvadrat
            på 45 × 45 m får plats - utan byggnader eller vatten (gul kant). Alla gator
            och vägar räknas som öppen mark, och trädtäckning ignoreras.
          </p>
        </div>

        <div className="os-controls">
          <button onClick={startSelection} className={selecting ? 'active-mode' : ''} disabled={isAnalyzing} data-tip="os.select">
            {selecting ? 'Klicka på kartan…' : 'Välj område'}
          </button>
          {SHOW_PLAN && (<>
          <input
            ref={planInputRef}
            type="file"
            accept=".pdf,.zip,application/pdf,application/zip"
            style={{ display: 'none' }}
            onChange={(e) => handlePlanChosen(e.target.files?.[0] || null)}
          />
          <button onClick={() => planInputRef.current?.click()} className={`os-plan-btn ${planFile ? 'has-file' : ''}`} disabled={isAnalyzing || planLocating} data-tip="os.plan">
            {planLocating ? 'Placerar plankartan…' : planFile ? `Plankarta: ${planFile.name}` : 'Lägg till plankarta'}
          </button>
          {planFile && (
            <button onClick={() => { setPlanFile(null); setPlanUpload(null); setPlanGeojson(null); setPlan(null); if (planInputRef.current) planInputRef.current.value = ''; }} className="os-plan-clear" disabled={isAnalyzing || planLocating} data-tip="os.planClear" aria-label="Ta bort plankartan">
              ×
            </button>
          )}
          </>)}
          <button onClick={handleAnalyze} disabled={!bounds || isAnalyzing} className="os-analyze-btn" data-tip="os.analyze">
            {isAnalyzing ? 'Analyserar…' : 'Analysera'}
          </button>
          <button onClick={exportPng} disabled={!bounds || isAnalyzing} className="os-export-btn" data-tip="os.png">
            Spara som PNG
          </button>
          <button onClick={startStreetView} className={`os-sv-btn ${svMode ? 'active-mode' : ''}`} disabled={isAnalyzing} data-tip="os.streetview">
            {svMode ? (svPoint ? 'Välj riktning…' : 'Klicka på kartan…') : 'Gatuvy'}
          </button>
          {svPoint && svTarget && !svMode && (
            <button onClick={() => openStreetView(svPoint, svTarget)} className="os-sv-btn" disabled={isAnalyzing} data-tip="os.streetviewAgain">
              Öppna gatuvy igen
            </button>
          )}
          {bounds && (
            <span className={`os-area-info ${selAreaKm2 > MAX_AREA_KM2 ? 'os-too-big' : ''}`} data-tip="os.area">
              Valt område: {selAreaKm2.toFixed(2).replace('.', ',')} km² (max {MAX_AREA_KM2} km²)
            </span>
          )}
          <button onClick={() => setOnboardingOpen(true)} className="os-help-btn" data-tip="os.help">
            Så fungerar det
          </button>
        </div>

        <OpenSpaceOnboarding open={onboardingOpen} onClose={closeOnboarding} />

        {(features.length > 0 || planGeojson) && (
          <div className="os-layer-toggles">
            <span className="os-checkbox os-legend-item">
              <span className="os-legend-swatch os-legend-open" /> Tomma ytor
            </span>
            <label className="os-checkbox" data-tip="os.showNumbers">
              <input type="checkbox" checked={showNumbers} onChange={(e) => setShowNumbers(e.target.checked)} />
              Visa nummer på kartan
            </label>
            {SHOW_PLAN && planGeojson && (
              <label className="os-checkbox" data-tip="os.showPlan">
                <input type="checkbox" checked={showPlan} onChange={(e) => setShowPlan(e.target.checked)} />
                <span className="os-legend-swatch os-legend-byggratt" /> Visa planen
              </label>
            )}
          </div>
        )}

        {SHOW_PLAN && scenarioNames.length > 0 && (
          <div className="os-scenario" data-tip="os.scenario">
            <span className="os-scenario-label">Scenario:</span>
            {['now', ...scenarioNames].map((name) => (
              <button
                key={name}
                className={`os-scenario-btn ${scenario === name ? 'active' : ''}`}
                onClick={() => { setScenario(name); setSelectedFeature(null); }}
                data-tip={name === 'now' ? 'os.scenarioNow' : `os.scenario.${name}`}
              >
                {name === 'now' ? 'Idag' : SCENARIO_LABELS[name] || name}
              </button>
            ))}
          </div>
        )}
        {SHOW_PLAN && plan?.error && !scenarioNames.length && (
          <div className="os-error">Plankartan: {plan.error}</div>
        )}

        {error && <div className="os-error">{error}</div>}
        {status && (
          <div className={`os-status ${busy ? 'os-status-busy' : ''}`}>
            {busy && <span className="os-spinner" aria-hidden="true" />}
            <span>{status}</span>
            {busy && (
              <span className="os-elapsed">{elapsed} s{busy.expect ? ` · ${busy.expect}` : ''}</span>
            )}
          </div>
        )}

        <div className="os-map-container">
          <GoogleMap
            mapContainerClassName="os-map"
            center={DEFAULT_LOCATION}
            zoom={14}
            onLoad={(map) => { mapRef.current = map; }}
            onClick={handleMapClick}
            onMouseMove={handleMouseMove}
            onCenterChanged={handleCenterChanged}
            options={{
              streetViewControl: false,
              mapTypeControl: true,
              mapTypeId: 'hybrid',
              draggableCursor: (selecting || svMode) ? 'crosshair' : undefined,
            }}
          >
            {selBounds && (
              <Rectangle
                bounds={selBounds}
                onLoad={(r) => { rectRef.current = r; }}
                onUnmount={() => { rectRef.current = null; }}
                onBoundsChanged={handleRectBoundsChanged}
                options={{
                  fillColor: '#2979FF',
                  fillOpacity: 0.06,
                  strokeColor: '#2979FF',
                  strokeWeight: 4,
                  editable: !isAnalyzing && !!bounds,
                  draggable: !isAnalyzing && !!bounds,
                  clickable: false,
                }}
              />
            )}

            {/* Ground dimensions of the box, shown at its top edge */}
            {selBounds && dimsLabel && (
              <Marker
                position={{
                  lat: selBounds.north,
                  lng: (selBounds.west + selBounds.east) / 2,
                }}
                clickable={false}
                label={{
                  text: dimsLabel,
                  color: '#2979FF',
                  fontSize: '15px',
                  fontWeight: 'bold',
                  className: 'os-dims-label',
                }}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 0,
                }}
              />
            )}

            {/* Detaljplan overlay: plan area outline, byggrätt (max footprint)
                and illustrated buildings */}
            {planLayer('planomrade').map((f, i) => (
              <Polygon
                key={`pa${i}`}
                paths={f.geometry.coordinates.map(r => r.map(([lng, lat]) => ({ lat, lng })))}
                options={{ fillOpacity: 0, strokeColor: '#37474F', strokeWeight: 3, clickable: false, zIndex: 1 }}
              />
            ))}
            {planLayer('byggratt').map((f, i) => (
              <Polygon
                key={`br${i}`}
                paths={f.geometry.coordinates.map(r => r.map(([lng, lat]) => ({ lat, lng })))}
                options={{ fillColor: '#FF8F00', fillOpacity: scenario === 'byggratt' ? 0.55 : 0.25, strokeColor: '#E65100', strokeWeight: 1.5, clickable: false, zIndex: 2 }}
              />
            ))}
            {SHOW_ILLUSTRATION && planLayer('plan_building').map((f, i) => (
              <Polygon
                key={`pb${i}`}
                paths={f.geometry.coordinates.map(r => r.map(([lng, lat]) => ({ lat, lng })))}
                options={{ fillColor: '#6D4C41', fillOpacity: scenario === 'illustration' ? 0.7 : 0.35, strokeColor: '#3E2723', strokeWeight: 1, clickable: false, zIndex: 3 }}
              />
            ))}

            {[[showOpen ? openFeatures : [], OPEN_STYLE, 'o'],
              [showParks ? parkFeatures : [], PARK_STYLE, 'p']].map(([list, style, kind]) =>
              list.map((f, i) => (
                <Polygon
                  key={`${kind}${i}`}
                  paths={f.geometry.coordinates.map(r => r.map(([lng, lat]) => ({ lat, lng })))}
                  onClick={(e) => (svMode ? handleMapClick(e) : setSelectedFeature(f))}
                  options={{
                    fillColor: style.fill,
                    fillOpacity: style.fillOpacity,
                    strokeColor: style.stroke,
                    strokeWeight: style.weight,
                  }}
                />
              ))
            )}

            {/* Numbered badges matching the results table rows (P = park) */}
            {showNumbers && [[showOpen ? openFeatures : [], OPEN_STYLE, ''],
              [showParks ? parkFeatures : [], PARK_STYLE, 'P']].map(([list, style, prefix]) =>
              list.map((f, i) => (
                <Marker
                  key={`n${prefix}${i}`}
                  position={featureCenter(f)}
                  onClick={() => setSelectedFeature(f)}
                  label={{
                    text: `${prefix}${i + 1}`,
                    color: '#ffffff',
                    fontSize: prefix ? '12px' : '14px',
                    fontWeight: 'bold',
                  }}
                  icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 12,
                    fillColor: style.badge,
                    fillOpacity: 1,
                    strokeColor: '#ffffff',
                    strokeWeight: 2,
                  }}
                />
              ))
            )}

            {/* Street View viewpoint: arrow pointing in the chosen direction,
                rubber-band line while the direction is being picked */}
            {svPoint && (
              <Polyline
                path={svArrowPath()}
                options={{
                  strokeColor: '#1E88E5',
                  strokeOpacity: 0.9,
                  strokeWeight: 4,
                  clickable: false,
                  icons: [{
                    icon: { path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 5, strokeColor: '#1E88E5', fillColor: '#1E88E5', fillOpacity: 1 },
                    offset: '100%',
                  }],
                }}
              />
            )}
            {svPoint && (
              <Marker
                position={svPoint}
                clickable={!svMode}
                onClick={() => svTarget && openStreetView(svPoint, svTarget)}
                title="Gatuvy - klicka för att öppna"
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 9,
                  fillColor: '#1E88E5',
                  fillOpacity: 1,
                  strokeColor: '#ffffff',
                  strokeWeight: 2,
                }}
              />
            )}

            {selectedFeature && (
              <InfoWindow
                position={featureCenter(selectedFeature)}
                onCloseClick={() => setSelectedFeature(null)}
              >
                <div className="os-infowindow">
                  <strong>{selectedFeature.properties.label || (isPark(selectedFeature) ? 'Park' : 'Öppen yta')}</strong>
                  {isPark(selectedFeature) && <div>Mindre park/grönyta (under 45 × 45 m)</div>}
                  <div>Area: {formatArea(selectedFeature.properties.areaM2)}</div>
                  <div>Största kvadrat: ca {selectedFeature.properties.maxSquareM} × {selectedFeature.properties.maxSquareM} m</div>
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        </div>

        {svOpen && (
          <div className="os-sv-overlay" onClick={closeStreetView}>
            <div className="os-sv-modal" onClick={(e) => e.stopPropagation()}>
              <div className="os-sv-head">
                <strong>Gatuvy</strong>
                {svPano && (
                  <span className="os-sv-meta">
                    Riktning {Math.round(svPano.heading)}° ({compassName(svPano.heading)})
                    {' · '}
                    {svPano.position.lat.toFixed(5)}, {svPano.position.lng.toFixed(5)}
                  </span>
                )}
                <button className="os-sv-close" onClick={closeStreetView} aria-label="Stäng" data-tip="os.svClose">×</button>
              </div>
              {svError
                ? <div className="os-sv-error">{svError}</div>
                : !svPano
                  ? <div className="os-sv-loading">Hämtar gatuvy…</div>
                  : null}
              {svPano && !svError && <div className="os-sv-pano" ref={svDivRef} />}
            </div>
          </div>
        )}

        {view && (features.length > 0 || view.footprintAreaM2 > 0) && (
          <div className="os-results">
            <h2>
              <span className="os-legend-swatch os-legend-open" />
              {view.count} ytor - totalt {formatArea(view.totalAreaM2)}
              {scenarioInfo && <span className="os-scenario-tag">{SCENARIO_LABELS[scenario]}</span>}
            </h2>
            {SHOW_PLAN && plan && (
              <p className="os-plan-info">
                {plan.filename ? `Plankarta: ${plan.filename}. ` : 'Plankarta: '}
                {plan.georef?.method === 'crosses'
                  ? `Placerad med ${plan.georef.crosses} koordinatkryss (skala 1:${plan.georef.scaleDenominator}). `
                  : plan.georef?.method === 'grid'
                    ? `Placerad via koordinatnätet (${plan.georef.crosses} kryss, ${plan.georef.gridM} m rutor, ${plan.georef.labelledAxes === 'N' ? 'bara N-värden' : plan.georef.labelledAxes === 'E' ? 'bara E-värden' : 'inga koordinatvärden'} läsbara) och grundkartans linjer mot Lantmäteriets byggnader – kontrollera läget mot satellitbilden. `
                    : plan.georef?.method === 'lm_match'
                      ? `Placerad genom att grundkartans byggnader matchats mot Lantmäteriets (${plan.georef.insideFootprint} av ${plan.georef.xBuildings} träffar, skala 1:${plan.georef.scaleDenominator}). `
                      : ''}
                {plan.stats && `Planområde ${formatArea(plan.stats.planAreaM2)}, kvartersmark ${formatArea(plan.stats.kvartersmarkM2)}, byggrätt ${formatArea(plan.stats.byggrattM2)}. `}
                {plan.illustration
                  ? `Illustrationsplan: ${plan.illustration.buildings} byggnader inpassade (${Math.round((plan.illustration.insideKvartersmark || 0) * 100)} % på kvartersmark). `
                  : ''}
                {plan.error && <span className="os-plan-error">{plan.error}</span>}
              </p>
            )}
            {(view.emptySpaceRatio != null || view.urbanCoverageRatio != null) && (
              <dl className="os-metrics">
                {view.emptySpaceRatio != null && (
                  <div data-tip="os.esr">
                    <dt>Empty Space Ratio</dt>
                    <dd>
                      {view.emptySpaceRatio.toLocaleString('sv-SE', { maximumFractionDigits: 1 })} %
                      {scenarioInfo && <span className="os-delta"> idag {fmtPct(summary.emptySpaceRatio)}</span>}
                      <small> öppna ytor inom rutan / rutans yta ({formatArea(view.openAreaInSelectionM2 || 0)} av rutan är byggnadsfri yta ≥ 45 × 45 m)</small>
                    </dd>
                  </div>
                )}
                {view.footprintAreaM2 > 0 && view.urbanCoverageRatio != null && (
                <div data-tip="os.ucr">
                  <dt>Urban Coverage Ratio</dt>
                  <dd>
                    {view.urbanCoverageRatio.toLocaleString('sv-SE', { maximumFractionDigits: 1 })} %
                    {scenarioInfo && <span className="os-delta"> idag {fmtPct(summary.urbanCoverageRatio)}</span>}
                    <small> byggnadsavtryck / rutans yta ({formatArea(view.footprintAreaM2)} bebyggt{scenarioInfo ? `, varav ${formatArea(scenarioInfo.newFootprintM2 || 0)} enligt planen` : ''})</small>
                  </dd>
                </div>
                )}
                {view.footprintAreaM2 > 0 && view.buildingSizeRatio != null && (
                <div data-tip="os.bsr">
                  <dt>Building Size Ratio</dt>
                  <dd>
                    {view.buildingSizeRatio.toLocaleString('sv-SE', { maximumFractionDigits: 2 })}
                    <small> byggnader per 100 m² avtryck ({view.buildingCount} byggnader, i snitt {formatArea(view.meanFootprintM2)} per byggnad)</small>
                  </dd>
                </div>
                )}
              </dl>
            )}
            {openFeatures.length > 0 && (
              <table className="os-table">
                <thead>
                  <tr><th>#</th><th>Typ</th><th>Area</th><th>Största kvadrat</th></tr>
                </thead>
                <tbody>
                  {openFeatures.map((f, i) => (
                    <tr key={i} onClick={() => focusFeature(f)} data-tip="os.row"
                        className={selectedFeature === f ? 'os-row-selected' : ''}>
                      <td>{i + 1}</td>
                      <td>{f.properties.label || '-'}</td>
                      <td>{formatArea(f.properties.areaM2)}</td>
                      <td>{f.properties.maxSquareM} × {f.properties.maxSquareM} m</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {parkFeatures.length > 0 && (
              <>
                <h2 className="os-parks-heading">
                  <span className="os-legend-swatch os-legend-park" />
                  {parkFeatures.length} mindre parker/grönytor under 45 × 45 m
                </h2>
                <table className="os-table">
                  <thead>
                    <tr><th>#</th><th>Typ</th><th>Area</th><th>Största kvadrat</th></tr>
                  </thead>
                  <tbody>
                    {parkFeatures.map((f, i) => (
                      <tr key={`p${i}`} onClick={() => focusFeature(f)} data-tip="os.row"
                          className={selectedFeature === f ? 'os-row-selected' : ''}>
                        <td>P{i + 1}</td>
                        <td>{f.properties.label || 'Park'}</td>
                        <td>{formatArea(f.properties.areaM2)}</td>
                        <td>{f.properties.maxSquareM} × {f.properties.maxSquareM} m</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="os-disclaimer">
                  Mindre parker bygger på OpenStreetMaps klassning (park, gräsyta, lekplats,
                  trädgård, hundrastgård) och kan saknas där kartan är ofullständig.
                </p>
              </>
            )}
            <p className="os-disclaimer">
              {'Ytorna är fria från byggnader och vatten; trädtäckning ignoreras och kan förekomma. '}
              {view.buildingSource === 'lm+osm'
                ? 'Byggnader från Lantmäteriet (fullständiga byggnadsavtryck) och OpenStreetMap; vatten från OpenStreetMap. '
                : 'Byggnader och vatten kommer från OpenStreetMap. '}
              Alla gator och vägar räknas som öppen mark. Ytor kan vara privata
              eller ha annan markanvändning - verifiera alltid mot flygfoto (satellitvyn)
              innan slutsatser dras.
            </p>
            <p className="os-disclaimer os-attribution">{DATA_ATTRIBUTION(view.buildingSource)}</p>
          </div>
        )}

        {previous.length > 0 && (
          <div className="os-previous">
            <h3>Tidigare analyser</h3>
            <ul>
              {previous.map((a) => (
                <li key={a.analysisId}>
                  <button onClick={() => loadPreviousAnalysis(a.analysisId)} disabled={isAnalyzing} data-tip="os.previous">
                    {a.name ? `${a.name} - ` : ''}{new Date(a.timestamp).toLocaleString('sv-SE')} - {a.spaceCount ?? '?'} ytor
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </>
  );
};

export default OpenSpaceAnalyzer;
